import React, { useState, useEffect } from 'react'

import styles from '../../TechPartnerDashboard/TechPartnerDashboard.module.scss'

import {
    fetchTechPartnerFulfillmentProcessPurchaseData,
    fetchTechPartnerFulfillmentProcessInventoryData,
    FulfillmentMonthlyPurchaseData,
    FulfillmentInventorySummaryData,
} from '../../../features/sales/salesAPI'

import { TechPartnerDashboardDataSectionProps } from '../TechPartnerDashboardSection'

export enum InventoryFulfillmentTypes {
    PURCHASES = 0,
    FACTORYSHIPMENTS = 1,
    OPENPOS = 2,
    INVENTORY = 3,
    EMPTYROW = 4,
}

export const FulfillmentProcessData = (props: TechPartnerDashboardDataSectionProps) => {
    interface FulfillmentInventoryTypeData {
        FulfillmentType: InventoryFulfillmentTypes
        Data: Array<FulfillmentMonthlyPurchaseData>
        SortOrder: number
    }

    const [fulfillmentProcessDataPayload, setFulfillmentProcessDataPayload] = useState<
        Array<FulfillmentInventoryTypeData>
    >([])

    interface InventoryFulfillmentProcess {
        FulfillmentType: InventoryFulfillmentTypes
        TitlePrimary: string
        TitleSecondary: string
        ReportPeriodCY: number
        CalendarYTD: number
        ReportPeriodPY?: number
        CalendarPYTD?: number
        ReportPeriodDelta?: number
        CalendarDelta?: number
    }

    const [inventoryFulfillmentProcessData, setInventoryFulfillmentProcessData] = useState<
        Array<InventoryFulfillmentProcess>
    >([])

    const [inventoryFulfillmentSummaryData, setInventoryFulfillmentSummaryData] = useState<
        Array<FulfillmentInventorySummaryData>
    >([])

    const [pastDuePOs, setPastDuePOs] = useState<number>(0)
    const [turns, setTurns] = useState<number>(0)

    const InventoryFulfillmentTypesCount = Object.keys(InventoryFulfillmentTypes).length / 2
    interface RowTitle {
        FulfillmentType: InventoryFulfillmentTypes
        PrimaryTitle: string
        SecondaryTitle: string
    }
    const rowTitles: Array<RowTitle> = [
        {
            FulfillmentType: InventoryFulfillmentTypes.PURCHASES,
            PrimaryTitle: 'Purchases',
            SecondaryTitle: 'Orders to factory',
        },
        {
            FulfillmentType: InventoryFulfillmentTypes.FACTORYSHIPMENTS,
            PrimaryTitle: 'Factory Shipments',
            SecondaryTitle: 'Received from factory',
        },
        {
            FulfillmentType: InventoryFulfillmentTypes.OPENPOS,
            PrimaryTitle: 'Open POs',
            SecondaryTitle: 'Orders not received from factory',
        },
    ]

    useEffect(() => {
        if (Object.keys(props.selectedInputs).length === 0) return

        props.onLoadingChange(true)

        let fulfillmentData: Array<FulfillmentInventoryTypeData> = []
        let fulfillmentInventorySummaryData: Array<FulfillmentInventorySummaryData> = []
        let pastDuePOs: number = 0
        let turns: number = 0
        let asyncFunctions: (() => Promise<void>)[] = []
        for (let i = 0; i < InventoryFulfillmentTypesCount; i++) {
            asyncFunctions.push(async () => {
                if (Object.keys(props.selectedInputs).length === 0) return

                switch (i) {
                    case InventoryFulfillmentTypes.PURCHASES:
                    case InventoryFulfillmentTypes.FACTORYSHIPMENTS:
                    case InventoryFulfillmentTypes.OPENPOS:
                        let purchasingPayload =
                            await fetchTechPartnerFulfillmentProcessPurchaseData({
                                Inputs: props.selectedInputs,
                                InventoryFulfillmentType: i,
                            })
                        fulfillmentData.push({
                            FulfillmentType: i,
                            Data: purchasingPayload.MonthlyData,
                            SortOrder: i,
                        })
                        pastDuePOs = purchasingPayload.PastDuePOs
                        break
                    case InventoryFulfillmentTypes.INVENTORY:
                        // case InventoryFulfillmentTypes.AVAILABLEINVENTORY:
                        let inventoryPayload =
                            await fetchTechPartnerFulfillmentProcessInventoryData({
                                Inputs: props.selectedInputs,
                                InventoryFulfillmentType: i,
                            })

                        if (inventoryPayload.Turns) turns = inventoryPayload.Turns

                        if (inventoryPayload.SummaryData)
                            fulfillmentInventorySummaryData = inventoryPayload.SummaryData

                        break
                }
            })
        }

        const getFulfillmentData = async () => {
            await Promise.all(
                asyncFunctions.map(async (get) => {
                    await get()
                })
            )
        }

        getFulfillmentData().then(() => {
            setFulfillmentProcessDataPayload(
                fulfillmentData.sort((a, b) => a.SortOrder - b.SortOrder)
            )
            setPastDuePOs(pastDuePOs)
            setTurns(turns)
            setInventoryFulfillmentSummaryData(fulfillmentInventorySummaryData)
        })
    }, [props.selectedInputs])

    useEffect(() => {
        calculateFulfillmentProcess()

        props.onLoadingChange(false)
    }, [fulfillmentProcessDataPayload])

    const calculateFulfillmentProcess = () => {
        const today = new Date()
        const CurrentYear = today.getFullYear()
        const CurrentMonth = today.getMonth()

        const ReportPeriodBeginYear = props.selectedInputs.ReportPeriodBegin
            ? props.selectedInputs.ReportPeriodBegin.getFullYear()
            : new Date().getFullYear()
        const ReportPeriodBeginMonth = props.selectedInputs.ReportPeriodBegin
            ? props.selectedInputs.ReportPeriodBegin.getMonth()
            : 0

        const ReportPeriodEndYear = props.selectedInputs.ReportPeriodEnd
            ? props.selectedInputs.ReportPeriodEnd.getFullYear()
            : today.getFullYear()
        const ReportPeriodEndMonth = props.selectedInputs.ReportPeriodEnd
            ? props.selectedInputs.ReportPeriodEnd.getMonth() - 1
            : 0

        const MonthDiff =
            ReportPeriodBeginMonth +
            12 * ReportPeriodBeginYear -
            (ReportPeriodEndMonth + 12 * ReportPeriodEndYear)

        const daysInMonth = (year: number, month: number) => {
            return new Date(year, month, 0).getDate()
        }
        let fractionOfTheMonth =
            (today.getDate() - 1) / daysInMonth(today.getFullYear(), today.getMonth() + 1)

        if (fulfillmentProcessDataPayload) {
            let fulfillmentProcessDisplayData: Array<InventoryFulfillmentProcess> = []
            for (let i = 0; i < fulfillmentProcessDataPayload.length; i++) {
                const fulfillmentProcessRow = fulfillmentProcessDataPayload[i]
                const dataPayload = fulfillmentProcessRow?.Data

                let rowTitle = rowTitles.find(
                    (t) => t.FulfillmentType === fulfillmentProcessRow?.FulfillmentType
                )
                let primaryTitle = ''
                let secondaryitle = ''
                if (rowTitle) {
                    primaryTitle = rowTitle.PrimaryTitle
                    secondaryitle = rowTitle.SecondaryTitle
                }

                let costYTDReportPeriod = dataPayload
                    ?.filter((month) => month.MonthAge >= MonthDiff && month.MonthAge <= 0)
                    .reduce((a, b) => a + b.Cost, 0)
                if (!costYTDReportPeriod) costYTDReportPeriod = 0

                let costYTDCalendar = dataPayload
                    ?.filter((month) => month.MonthAge >= -CurrentMonth && month.MonthAge <= 0)
                    .reduce((a, b) => a + b.Cost, 0)
                if (!costYTDCalendar) costYTDCalendar = 0

                let costPYReportPeriod = undefined
                let costPYTDCalendar = undefined
                let costReportPeriodDelta = undefined
                let costCalendarDelta = undefined

                costPYReportPeriod = dataPayload
                    ?.filter((month) => month.MonthAge >= MonthDiff - 12 && month.MonthAge <= -12)
                    .reduce(
                        (a, b) => a + (b.MonthAge !== -12 ? b.Cost : b.Cost * fractionOfTheMonth),
                        0
                    )
                if (!costPYReportPeriod) costPYReportPeriod = 0

                costPYTDCalendar = dataPayload
                    ?.filter(
                        (month) => month.MonthAge >= -(CurrentMonth + 12) && month.MonthAge <= -12
                    )
                    .reduce(
                        (a, b) => a + (b.MonthAge !== -12 ? b.Cost : b.Cost * fractionOfTheMonth),
                        0
                    )
                if (!costPYTDCalendar) costPYTDCalendar = 0

                costReportPeriodDelta = costYTDReportPeriod - costPYReportPeriod
                costCalendarDelta = costYTDCalendar - costPYTDCalendar

                if (fulfillmentProcessRow) {
                    fulfillmentProcessDisplayData.push({
                        FulfillmentType: fulfillmentProcessRow.FulfillmentType,
                        TitlePrimary: primaryTitle,
                        TitleSecondary: secondaryitle,
                        ReportPeriodCY: costYTDReportPeriod,
                        CalendarYTD: costYTDCalendar,
                        ReportPeriodPY: costPYReportPeriod,
                        CalendarPYTD: costPYTDCalendar,
                        ReportPeriodDelta: costReportPeriodDelta,
                        CalendarDelta: costCalendarDelta,
                    })
                }
            }

            setInventoryFulfillmentProcessData(fulfillmentProcessDisplayData)
        }
    }

    const renderFulfillmentProcessRows = () => {
        let fulfillmentProcessRows = []

        if (inventoryFulfillmentProcessData.length === 0)
            setInventoryFulfillmentProcessData(new Array<InventoryFulfillmentProcess>(3))
        for (let i = 0; i < inventoryFulfillmentProcessData.length; i++) {
            fulfillmentProcessRows.push(
                <React.Fragment key={`fulfillmentProcess${i}`}>
                    <div className={styles.dataLabel}>
                        <span className={styles.dataLabelPrimary}>
                            {inventoryFulfillmentProcessData[i]?.TitlePrimary}
                        </span>
                        <span className={styles.dataLabelSecondary}>
                            {inventoryFulfillmentProcessData[i]?.TitleSecondary &&
                                inventoryFulfillmentProcessData[i]?.TitleSecondary !== '' &&
                                `(${inventoryFulfillmentProcessData[i]?.TitleSecondary})`}
                        </span>
                    </div>
                    {inventoryFulfillmentProcessData[i]?.ReportPeriodCY > 0 ? (
                        <div className={styles.dataCurrency}>
                            {inventoryFulfillmentProcessData[i]?.ReportPeriodCY.toLocaleString(
                                'en-US',
                                {
                                    style: 'currency',
                                    currency: 'USD',
                                }
                            )}
                        </div>
                    ) : (
                        <div></div>
                    )}
                    <div className={styles.dataCurrency}>
                        {inventoryFulfillmentProcessData[i]?.ReportPeriodPY?.toLocaleString(
                            'en-US',
                            {
                                style: 'currency',
                                currency: 'USD',
                            }
                        )}
                    </div>
                    <div className={styles.dataCurrency}>
                        {inventoryFulfillmentProcessData[i]?.ReportPeriodDelta?.toLocaleString(
                            'en-US',
                            {
                                style: 'currency',
                                currency: 'USD',
                            }
                        )}
                    </div>
                    {inventoryFulfillmentProcessData[i]?.ReportPeriodPY &&
                    inventoryFulfillmentProcessData[i]?.ReportPeriodPY! > 0 ? (
                        <div className={styles.dataCurrency}>
                            {(
                                (inventoryFulfillmentProcessData[i]?.ReportPeriodDelta
                                    ? inventoryFulfillmentProcessData[i]?.ReportPeriodDelta!
                                    : 0) / inventoryFulfillmentProcessData[i]?.ReportPeriodPY!
                            ).toLocaleString('en-US', {
                                style: 'percent',
                                maximumFractionDigits: 0,
                                minimumFractionDigits: 0,
                            })}
                        </div>
                    ) : (
                        <div></div>
                    )}
                    {inventoryFulfillmentProcessData[i]?.CalendarYTD > 0 ? (
                        <div className={styles.dataCurrency}>
                            {inventoryFulfillmentProcessData[i]?.CalendarYTD.toLocaleString(
                                'en-US',
                                {
                                    style: 'currency',
                                    currency: 'USD',
                                }
                            )}
                        </div>
                    ) : (
                        <div></div>
                    )}
                    <div className={styles.dataCurrency}>
                        {inventoryFulfillmentProcessData[i]?.CalendarPYTD?.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        })}
                    </div>
                    <div className={styles.dataCurrency}>
                        {inventoryFulfillmentProcessData[i]?.CalendarDelta?.toLocaleString(
                            'en-US',
                            {
                                style: 'currency',
                                currency: 'USD',
                            }
                        )}
                    </div>
                    {inventoryFulfillmentProcessData[i]?.CalendarPYTD &&
                    inventoryFulfillmentProcessData[i]?.CalendarPYTD! > 0 ? (
                        <div className={styles.dataCurrency}>
                            {(
                                (inventoryFulfillmentProcessData[i]?.CalendarDelta
                                    ? inventoryFulfillmentProcessData[i]?.CalendarDelta!
                                    : 0) / inventoryFulfillmentProcessData[i]?.CalendarPYTD!
                            ).toLocaleString('en-US', {
                                style: 'percent',
                                maximumFractionDigits: 0,
                                minimumFractionDigits: 0,
                            })}
                        </div>
                    ) : (
                        <div></div>
                    )}
                    {i === 2 ? (
                        <div>
                            <div className={`${styles.split} ${styles.pastDuePOs}`}>
                                <div>Total Past Due POs:</div>
                                <div className={styles.dataCurrency}>
                                    {pastDuePOs.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                    })}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div></div>
                    )}
                </React.Fragment>
            )
        }
        return fulfillmentProcessRows
    }

    const renderFulfillmentInventorySummaryRows = () => {
        let fulfillmentInventorySummaryRows = []

        if (inventoryFulfillmentSummaryData.length === 0)
            setInventoryFulfillmentSummaryData(new Array<FulfillmentInventorySummaryData>(3))
        for (let i = 0; i < inventoryFulfillmentSummaryData.length; i++) {
            fulfillmentInventorySummaryRows.push(
                <React.Fragment key={`inventoryFulfillmentSummary${i}`}>
                    <div className={styles.dataLabel}>
                        <span className={styles.dataLabelPrimary}>
                            {inventoryFulfillmentSummaryData[i]?.TitlePrimary}
                        </span>
                        <span className={styles.dataLabelSecondary}>
                            {inventoryFulfillmentSummaryData[i]?.TitleSecondary &&
                                inventoryFulfillmentSummaryData[i]?.TitleSecondary !== '' &&
                                `(${inventoryFulfillmentSummaryData[i]?.TitleSecondary})`}
                        </span>
                    </div>
                    <div className={styles.dataCurrency}>
                        {inventoryFulfillmentSummaryData[i]?.TotalCost.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        })}
                    </div>
                    <div className={styles.dataCurrency}>
                        {inventoryFulfillmentSummaryData[i] &&
                            (
                                inventoryFulfillmentSummaryData[i]?.TotalCost -
                                inventoryFulfillmentSummaryData[i]?.AvailableCost
                            ).toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                            })}
                    </div>
                    <div className={styles.dataCurrency}>
                        {inventoryFulfillmentSummaryData[i]?.AvailableCost.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        })}
                    </div>
                    {i === 2 ? (
                        <div className={styles.dataCurrency}>
                            {inventoryFulfillmentSummaryData[i] &&
                                turns.toLocaleString('en-US', {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 2,
                                })}
                        </div>
                    ) : (
                        <div></div>
                    )}
                    <div className={styles.dataCurrency}>
                        {/* {inventoryFulfillmentSummaryData[i]?.DeadCost.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        })} */}
                    </div>
                    <div className={styles.dataCurrency}>
                        {inventoryFulfillmentSummaryData[i]?.Aged012Cost.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        })}
                    </div>
                    <div className={styles.dataCurrency}>
                        {inventoryFulfillmentSummaryData[i]?.Aged1324Cost.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        })}
                    </div>
                    <div className={styles.dataCurrency}>
                        {inventoryFulfillmentSummaryData[i]?.Aged2536Cost.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        })}
                    </div>
                    <div className={styles.dataCurrency}>
                        {inventoryFulfillmentSummaryData[i]?.AgedOver36Cost.toLocaleString(
                            'en-US',
                            {
                                style: 'currency',
                                currency: 'USD',
                            }
                        )}
                    </div>
                </React.Fragment>
            )
        }
        return fulfillmentInventorySummaryRows
    }

    return (
        <div className={styles.fulfillmentProcess} id='fulfillment'>
            <div className={styles.dataContainer}>
                <div className={styles.titleRow}>
                    <div className={styles.alignLeft}>{/* Inventory */}</div>
                    <div>Report Period CY</div>
                    <div>Report Period PY</div>
                    <div>Report Period Delta $</div>
                    <div>Report Period % Change</div>
                    <div>Calendar YTD</div>
                    <div>Calendar PYTD</div>
                    <div>Calendar Delta $</div>
                    <div>Calendar % Change</div>
                    <div></div>
                </div>
                <div className={styles.dataRows}>{renderFulfillmentProcessRows()}</div>
            </div>
            <div className={`${styles.dataContainer} ${styles.inventoryValues}`}>
                {/* Row 1 */}
                <div className={styles.titleRow}>
                    <div className={styles.dataLabel}>
                        <span className={styles.dataLabelPrimary}>Inventory</span>
                        <span
                            className={styles.fulfillmentSecondaryTitle}
                            style={{ marginLeft: '15px' }}
                        >
                            Current as of: {new Date().toLocaleString()}
                        </span>
                    </div>
                    <div>Total</div>
                    <div>Allocated</div>
                    <div>Available for Sale</div>
                    <div>Turns</div>
                    <div>Aged</div>
                    <div>0-12 Months</div>
                    <div>13-24 Months</div>
                    <div>25-36 Months</div>
                    <div>36+ Months</div>
                </div>
                <div className={styles.dataRows}>{renderFulfillmentInventorySummaryRows()}</div>
            </div>
        </div>
    )
}

export default FulfillmentProcessData
